import { useMemo, useState } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import '../css/ExportSummaryChart.css';

/**
 * Two-ring donut summarising an export.
 *
 * The inner ring is where the money came from (Court Bookings / Packages /
 * Open Play — the workbook's three sheets) and the outer ring is what makes
 * each of those up (court hours, equipment rental, package price, player
 * fees, processing fee). Reading outward answers "how much, and of what",
 * which the six stat cards this replaced could not: they showed a single
 * Total Paid figure with no sight of the rental income inside it.
 *
 * Deliberately part-to-whole and nothing else — three inner segments and at
 * most seven outer ones, all directly labelled, with the full numbers in the
 * table below the chart rather than crowded onto the arcs.
 */

// One hue per source, indexed by the order the groups arrive in — Court
// Bookings, Packages & Events, Open Play. Assigned by identity, never by
// rank or by what survived the filter, so unticking a status can't repaint
// the sources that remain.
//
// These are three slots of the validated categorical palette; the ordering
// (green / orange / blue) is the club's own. Re-validated as a set in both
// modes: worst all-pairs CVD deltaE 9.2 light / 9.4 dark, worst
// normal-vision 24.0 / 20.9. Green sits at 2.74:1 on a white surface, under
// the 3:1 bar — which is why the legend, the direct labels and the value
// table are all present rather than optional.
const SERIES_COLORS = ['#1baf7a', '#eb6834', '#2a78d6'];

// Tints of the group hue for the outer ring, lightest last. Components are
// told apart by their labels, not by hue — cycling fresh hues for them would
// break the "one hue per entity" rule and read as seven unrelated things.
const COMPONENT_TINTS = [0.78, 0.52, 0.34];

// Two layouts, not one scaled down. On a phone the wide layout's leader
// labels shrink to a few unreadable pixels and the donut with them, so the
// narrow layout drops the labels entirely and spends the whole width on the
// rings — the legend above and the table below already carry every name and
// number, so nothing is lost but the lines.
const WIDE_GEOMETRY = {
  width: 760,
  height: 400,
  cx: 380,
  cy: 200,
  outerR: 152,
  outerInnerR: 118,
  innerR: 110,
  innerInnerR: 74,
  leaderLabels: true,
};

const COMPACT_GEOMETRY = {
  width: 300,
  height: 300,
  cx: 150,
  cy: 150,
  outerR: 142,
  outerInnerR: 112,
  innerR: 104,
  innerInnerR: 68,
  leaderLabels: false,
};

// The wide layout needs room for a label column on each side of the donut.
const COMPACT_QUERY = '(max-width: 720px)';

// Direct-labelled only above this share; anything smaller would collide with
// its neighbours and is carried by the table instead.
const LABEL_MIN_SHARE = 0.035;
const LABEL_ROW_HEIGHT = 17;

/**
 * Mixes a hex colour towards white.
 *
 * @param {string} hex e.g. '#2a78d6'
 * @param {number} amount 1 = the colour itself, 0 = white
 * @returns {string} hex
 */
const tint = (hex, amount) => {
  const value = parseInt(hex.slice(1), 16);
  const mixChannel = (channel) => Math.round(channel * amount + 255 * (1 - amount));
  const r = mixChannel((value >> 16) & 255);
  const g = mixChannel((value >> 8) & 255);
  const b = mixChannel(value & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

/**
 * A point on a circle, with 0 degrees at 12 o'clock.
 *
 * @param {object} geometry the active layout
 * @param {number} radius
 * @param {number} angle degrees, clockwise from the top
 * @returns {{x: number, y: number}}
 */
const polar = (geometry, radius, angle) => {
  const radians = ((angle - 90) * Math.PI) / 180;
  return {
    x: geometry.cx + radius * Math.cos(radians),
    y: geometry.cy + radius * Math.sin(radians),
  };
};

/**
 * An annular segment as an SVG path.
 *
 * @param {object} geometry the active layout
 * @param {number} outerRadius
 * @param {number} innerRadius
 * @param {number} startAngle degrees
 * @param {number} endAngle degrees
 * @returns {string}
 */
const segmentPath = (geometry, outerRadius, innerRadius, startAngle, endAngle) => {
  // A segment that covers the whole circle has identical start and end
  // points, which collapses an arc command to nothing — shaved so a
  // single-category export still draws a ring.
  const end = Math.min(endAngle, startAngle + 359.99);
  const large = end - startAngle > 180 ? 1 : 0;
  const outerStart = polar(geometry, outerRadius, startAngle);
  const outerEnd = polar(geometry, outerRadius, end);
  const innerEnd = polar(geometry, innerRadius, end);
  const innerStart = polar(geometry, innerRadius, startAngle);
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${large} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${large} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
};

/** A share as a whole percent, never rounded down to a misleading 0%. */
const share = (value, total) => {
  if (!total) return '0%';
  const percent = (value / total) * 100;
  return `${percent < 1 && percent > 0 ? '<1' : Math.round(percent)}%`;
};

/**
 * Pushes labels apart so they never overlap.
 *
 * Each label wants to sit at its segment's mid-angle; several small adjacent
 * segments want the same few pixels. Sorting by y and enforcing a minimum
 * gap moves the crowded ones just far enough, and the leader line still
 * points back at the arc they belong to.
 *
 * @param {Array<{y: number}>} labels one side of the chart
 * @param {number} chartHeight the viewBox height labels must stay inside
 * @returns {Array<object>} the same labels with `y` adjusted
 */
const spaceOutLabels = (labels, chartHeight) => {
  const sorted = [...labels].sort((a, b) => a.y - b.y);
  for (let i = 1; i < sorted.length; i += 1) {
    const needed = Math.max(sorted[i - 1].rowHeight || LABEL_ROW_HEIGHT, LABEL_ROW_HEIGHT);
    const gap = sorted[i].y - sorted[i - 1].y;
    if (gap < needed) sorted[i].y = sorted[i - 1].y + needed;
  }
  // A run pushed down off the bottom is walked back up as a block.
  const overflow = sorted.length > 0 ? sorted[sorted.length - 1].y - (chartHeight - 16) : 0;
  if (overflow > 0) sorted.forEach((label) => { label.y -= overflow; });
  return sorted;
};

/**
 * @param {object} props
 * @param {Array<{key: string, label: string, parts: Array<{key: string, label: string, value: number}>}>} props.groups
 *   One entry per workbook sheet, in sheet order.
 * @param {number} props.recordCount how many bookings the total covers
 * @param {Array<{key: string, label: string, count: number, tone: string}>} props.statusCounts
 *   Shown as chips beneath the chart — a count, not money, so deliberately
 *   not a slice of it.
 */
function ExportSummaryChart({ groups = [], recordCount = 0, statusCounts = [] }) {
  const { config } = useConfig();
  /** Whole units, no decimals — the chart is for proportions, the table has the cents. */
  // Defined per component rather than at module scope: the symbol comes from
  // Config -> System, so it needs `config` in hand. Call sites read the same
  // as they always did.
  const peso = (amount) => formatMoney(Math.round(Number(amount) || 0), config);

  const [hovered, setHovered] = useState(null);
  const [pointer, setPointer] = useState({ x: 0, y: 0 });
  const compact = useMediaQuery(COMPACT_QUERY);
  const geometry = compact ? COMPACT_GEOMETRY : WIDE_GEOMETRY;

  const model = useMemo(() => {
    const priced = groups.map((group, index) => {
      const parts = (group.parts || [])
        .map((part, partIndex) => ({
          ...part,
          value: Number(part.value) || 0,
          color: tint(SERIES_COLORS[index % SERIES_COLORS.length], COMPONENT_TINTS[partIndex] ?? 0.28),
        }))
        .filter(part => part.value > 0);
      return {
        ...group,
        color: SERIES_COLORS[index % SERIES_COLORS.length],
        parts,
        total: parts.reduce((sum, part) => sum + part.value, 0),
      };
    });

    const total = priced.reduce((sum, group) => sum + group.total, 0);
    const drawable = priced.filter(group => group.total > 0);

    const innerSegments = [];
    const outerSegments = [];
    let angle = 0;
    drawable.forEach((group) => {
      const sweep = (group.total / total) * 360;
      innerSegments.push({ ...group, startAngle: angle, endAngle: angle + sweep, ring: 'inner' });
      let partAngle = angle;
      group.parts.forEach((part) => {
        const partSweep = (part.value / total) * 360;
        outerSegments.push({
          ...part,
          groupLabel: group.label,
          startAngle: partAngle,
          endAngle: partAngle + partSweep,
          ring: 'outer',
        });
        partAngle += partSweep;
      });
      angle += sweep;
    });

    // Component names that appear under more than one source; only these
    // need their source spelled out on the chart.
    const labelCounts = {};
    drawable.forEach((group) => {
      group.parts.forEach((part) => { labelCounts[part.label] = (labelCounts[part.label] || 0) + 1; });
    });
    const ambiguous = new Set(Object.keys(labelCounts).filter(label => labelCounts[label] > 1));

    return { groups: priced, drawable, innerSegments, outerSegments, total, ambiguous };
  }, [groups]);

  const { total, innerSegments, outerSegments, ambiguous } = model;

  // Leader-line labels for the outer ring, split by side and de-collided.
  const outerLabels = useMemo(() => {
    if (!geometry.leaderLabels) return [];
    const left = [];
    const right = [];
    outerSegments.forEach((segment) => {
      if (segment.value / total < LABEL_MIN_SHARE) return;
      const mid = (segment.startAngle + segment.endAngle) / 2;
      const anchor = polar(geometry, geometry.outerR + 6, mid);
      const side = Math.sin(((mid - 90) * Math.PI) / 180) >= 0 ? 'right' : 'left';
      // `mid` between 0 and 180 is the right half of the circle.
      const onRight = mid <= 180;
      const entry = {
        key: `${segment.groupLabel}-${segment.key}`,
        label: segment.label,
        value: segment.value,
        color: segment.color,
        groupLabel: ambiguous.has(segment.label) ? segment.groupLabel : '',
        anchorX: anchor.x,
        anchorY: anchor.y,
        y: anchor.y,
        side: onRight ? 'right' : 'left',
      };
      entry.rowHeight = entry.groupLabel ? LABEL_ROW_HEIGHT + 12 : LABEL_ROW_HEIGHT;
      (onRight ? right : left).push(entry);
      return side;
    });
    return [
      ...spaceOutLabels(left, geometry.height),
      ...spaceOutLabels(right, geometry.height),
    ];
  }, [outerSegments, total, ambiguous, geometry]);

  if (total <= 0) {
    return (
      <div className="esc-card esc-empty">
        <p>No revenue in this selection.</p>
      </div>
    );
  }

  const trackPointer = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    setPointer({ x: event.clientX - bounds.left, y: event.clientY - bounds.top });
  };

  const hoverProps = (segment, label, sublabel) => ({
    onMouseEnter: () => setHovered({ label, sublabel, value: segment.value ?? segment.total }),
    onMouseLeave: () => setHovered(null),
    onFocus: () => setHovered({ label, sublabel, value: segment.value ?? segment.total }),
    onBlur: () => setHovered(null),
    tabIndex: 0,
    role: 'img',
    'aria-label': `${sublabel ? `${sublabel}, ` : ''}${label}: ${peso(segment.value ?? segment.total)}`,
  });

  return (
    <div className="esc-card">
      {/* Legend for the inner ring. Present because there are three series;
          it sits above the chart rather than inside it, where a label would
          run under the total in the hole. */}
      <ul className="esc-legend">
        {model.drawable.map(group => (
          <li key={group.key} className="esc-legend-item">
            <span className="esc-swatch" style={{ background: group.color }} aria-hidden="true" />
            <span className="esc-legend-name">{group.label}</span>
            <span className="esc-legend-value">{peso(group.total)}</span>
            <span className="esc-legend-share">{share(group.total, total)}</span>
          </li>
        ))}
      </ul>

      <div className="esc-chart-wrap" onMouseMove={trackPointer}>
        <svg
          className={`esc-chart${compact ? ' esc-chart-compact' : ''}`}
          viewBox={`0 0 ${geometry.width} ${geometry.height}`}
          role="group"
          aria-label="Revenue by source and component"
        >
          {/* Outer ring: the components. A 2px surface-coloured stroke gives
              the gap between neighbouring fills. */}
          {outerSegments.map(segment => (
            <path
              key={`outer-${segment.groupLabel}-${segment.key}`}
              d={segmentPath(geometry, geometry.outerR, geometry.outerInnerR, segment.startAngle, segment.endAngle)}
              fill={segment.color}
              className="esc-segment"
              {...hoverProps(segment, segment.label, segment.groupLabel)}
            />
          ))}

          {/* Inner ring: the three workbook sheets. */}
          {innerSegments.map(segment => (
            <path
              key={`inner-${segment.key}`}
              d={segmentPath(geometry, geometry.innerR, geometry.innerInnerR, segment.startAngle, segment.endAngle)}
              fill={segment.color}
              className="esc-segment"
              {...hoverProps(segment, segment.label)}
            />
          ))}

          {/* Leader lines and labels for the outer ring. */}
          {outerLabels.map((label) => {
            const elbowX = label.side === 'right'
              ? geometry.cx + geometry.outerR + 34
              : geometry.cx - geometry.outerR - 34;
            const textX = label.side === 'right' ? elbowX + 8 : elbowX - 8;
            return (
              <g key={label.key} className="esc-leader">
                <polyline
                  points={`${label.anchorX},${label.anchorY} ${elbowX},${label.y} ${textX - (label.side === 'right' ? 4 : -4)},${label.y}`}
                  fill="none"
                />
                <circle cx={label.anchorX} cy={label.anchorY} r="2.5" fill={label.color} />
                <text
                  x={textX}
                  y={label.y}
                  className="esc-leader-label"
                  textAnchor={label.side === 'right' ? 'start' : 'end'}
                  dominantBaseline="middle"
                >
                  {label.groupLabel && (
                    <tspan x={textX} dy="-7" className="esc-leader-group">{label.groupLabel}</tspan>
                  )}
                  <tspan x={textX} dy={label.groupLabel ? '13' : '0'}>
                    {label.label} <tspan className="esc-leader-value">{peso(label.value)}</tspan>
                  </tspan>
                </text>
              </g>
            );
          })}

          {/* The headline figure, in the hole. */}
          <text x={geometry.cx} y={geometry.cy - 14} className="esc-center-label" textAnchor="middle">TOTAL</text>
          <text x={geometry.cx} y={geometry.cy + 16} className="esc-center-value" textAnchor="middle">{peso(total)}</text>
          <text x={geometry.cx} y={geometry.cy + 36} className="esc-center-sub" textAnchor="middle">
            {recordCount} booking{recordCount === 1 ? '' : 's'}
          </text>
        </svg>

        {hovered && (
          <div
            className="esc-tooltip"
            style={{ left: `${pointer.x}px`, top: `${pointer.y}px` }}
          >
            {hovered.sublabel && <span className="esc-tooltip-sub">{hovered.sublabel}</span>}
            <span className="esc-tooltip-label">{hovered.label}</span>
            <span className="esc-tooltip-value">{peso(hovered.value)} · {share(hovered.value, total)}</span>
          </div>
        )}
      </div>

      {statusCounts.length > 0 && (
        <div className="esc-status-row">
          {statusCounts.map(status => (
            <span key={status.key} className={`esc-status esc-status-${status.tone}`}>
              <span className="esc-status-count">{status.count}</span>
              {status.label}
            </span>
          ))}
        </div>
      )}

      {/* The values, in full. Identity is never colour alone, and the three
          light tints that sit under 3:1 on a white surface are legible here
          whatever the reader can see on the arcs. */}
      <div className="esc-table-wrap">
      <table className="esc-table">
        <caption className="esc-sr-only">Revenue by source and component</caption>
        <thead>
          <tr>
            <th scope="col">Source</th>
            <th scope="col">Component</th>
            <th scope="col" className="esc-num">Amount</th>
            <th scope="col" className="esc-num">Share</th>
          </tr>
        </thead>
        <tbody>
          {model.groups.map(group => (
            group.parts.map((part, index) => (
              <tr key={`${group.key}-${part.key}`}>
                {index === 0 ? (
                  <th scope="row" rowSpan={group.parts.length} className="esc-group-cell">
                    <span className="esc-swatch" style={{ background: group.color }} aria-hidden="true" />
                    {group.label}
                    <span className="esc-group-total">{peso(group.total)}</span>
                  </th>
                ) : null}
                <td>
                  <span className="esc-swatch esc-swatch-sm" style={{ background: part.color }} aria-hidden="true" />
                  {part.label}
                </td>
                <td className="esc-num">{peso(part.value)}</td>
                <td className="esc-num esc-muted">{share(part.value, total)}</td>
              </tr>
            ))
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" colSpan={2}>Total</th>
            <td className="esc-num">{peso(total)}</td>
            <td className="esc-num esc-muted">100%</td>
          </tr>
        </tfoot>
      </table>
      </div>
    </div>
  );
}

export default ExportSummaryChart;
